/* eslint-disable import/prefer-default-export */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';

import PermissionsList from './PermissionsList';
import { CreateTokenArgs } from '../../../types/PersonalAccessToken';

interface PersonalAccessTokenFormProps {
  permissions: string[];
  onCancel: () => void;
  onSubmit: (args: CreateTokenArgs) => void;
}

export function PersonalAccessTokenForm({
  permissions,
  onCancel,
  onSubmit,
}: PersonalAccessTokenFormProps) {
  const { t } = useTranslation();
  const [tokenName, setTokenName] = useState('');
  const [tokenDescription, setTokenDescription] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!tokenName) {
      setError(t('settings.api.personalAccessToken.errors.tokenNameRequired'));
      return;
    }
    if (selectedPermissions.length === 0) {
      setError(
        t('settings.api.personalAccessToken.errors.permissionsRequired'),
      );
      return;
    }
    setError(null); // Clear any previous errors

    const tokenArgs: CreateTokenArgs = {
      name: tokenName,
      description: tokenDescription,
      permissions: selectedPermissions,
    };

    if (expirationDate) {
      tokenArgs.expiresAt = new Date(expirationDate);
    }

    onSubmit(tokenArgs);
  };

  return (
    <div className="flex flex-col gap-4 mb-8">
      <h3 className="text-lg font-medium">
        {t('settings.api.personalAccessToken.title')}
      </h3>

      <div className="space-y-2">
        <Label htmlFor="token-name">
          {t('settings.api.personalAccessToken.form.tokenName.label')}
        </Label>
        <Input
          id="token-name"
          value={tokenName}
          onChange={(e) => setTokenName(e.target.value)}
          placeholder={t(
            'settings.api.personalAccessToken.form.tokenName.placeholder',
          )}
        />
        <p className="text-sm text-muted-foreground">
          {t('settings.api.personalAccessToken.form.tokenName.helperText')}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="token-description">
          {t('settings.api.personalAccessToken.form.tokenDescription.label')}
        </Label>
        <Textarea
          id="token-description"
          value={tokenDescription}
          onChange={(e) => setTokenDescription(e.target.value)}
          rows={4}
          placeholder={t(
            'settings.api.personalAccessToken.form.tokenDescription.placeholder',
          )}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="expiration-date">
          {t('settings.api.personalAccessToken.form.expirationDate.label')}
        </Label>
        <Input
          id="expiration-date"
          type="date"
          value={expirationDate}
          onChange={(e) => setExpirationDate(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label>{t('settings.api.personalAccessToken.form.scopes.label')}</Label>
        <PermissionsList
          onChange={setSelectedPermissions}
          permissions={permissions}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 mt-4">
        <Button
          variant="default"
          onClick={handleSubmit}
          disabled={!tokenName || selectedPermissions.length === 0}
        >
          {t('settings.api.personalAccessToken.buttons.createToken')}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t('settings.api.personalAccessToken.buttons.cancel')}
        </Button>
      </div>
    </div>
  );
}
